(() => {
  'use strict';

  const productSelector = '[data-aonsk-product-id]';

  window.aonskOrderCollectionPage = async (root, pageUrl, signal) => {
    if (!root) return;
    const panel = root.querySelector('[data-collection-stock]');
    const grid = root.querySelector('[data-aonsk-product-grid]');
    if (!panel || !grid || panel.dataset.ordered === 'true') return;
    const url = new URL(pageUrl, window.location.origin);
    const availability = url.searchParams.getAll('filter.v.availability');
    if (availability.length === 1 && ['0', '1'].includes(availability[0])) return;

    const stockCount = Number(panel.dataset.stockCount);
    const total = Number(panel.dataset.total);
    const soldCount = total - stockCount;
    const size = Number(panel.dataset.pageSize);
    const page = Number(panel.dataset.page);
    if (![stockCount, total, soldCount, size, page].every(Number.isFinite)
        || stockCount < 0 || soldCount < 0 || size < 1 || page < 1) return;
    if (!stockCount || !soldCount) {
      panel.dataset.ordered = 'true';
      return;
    }

    const start = (page - 1) * size;
    const end = Math.min(start + size, total);
    const original = Array.from(grid.querySelectorAll(productSelector));
    const livePanel = panel.isConnected;
    const requests = new Map();
    const fetchGroup = (mode, groupPage) => {
      const key = `${mode}:${groupPage}`;
      if (!requests.has(key)) {
        const target = new URL(url);
        target.searchParams.delete('stock');
        target.searchParams.set('filter.v.availability', mode);
        target.searchParams.set(panel.dataset.pageParam || 'page', String(groupPage));
        target.searchParams.set('section_id', panel.dataset.sectionId);
        requests.set(key, (async () => {
          const response = await fetch(target.href, { signal });
          if (!response.ok) throw new Error('Collection ordering request failed.');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const cards = Array.from(parsed.querySelectorAll(`[data-aonsk-product-grid] ${productSelector}`));
          if (!cards.length) throw new Error('Collection ordering returned no products.');
          return cards;
        })());
      }
      return requests.get(key);
    };

    const groupSlice = async (mode, offset, count) => {
      if (!count) return [];
      const cards = [];
      const overlap = stockCount + Number(panel.dataset.soldCount) !== total;
      if (mode === '0' && overlap) {
        const unique = new Set();
        const matching = [];
        const maxPage = Math.ceil(Number(panel.dataset.soldCount) / size);
        for (let n = 1; n <= maxPage && matching.length < offset + count; n += 1) {
          const batch = await fetchGroup(mode, n);
          batch.forEach((card) => {
            if (card.dataset.aonskAvailable === 'false' && !unique.has(card.dataset.aonskProductId)) {
              unique.add(card.dataset.aonskProductId);
              matching.push(card);
            }
          });
        }
        return matching.slice(offset, offset + count);
      }
      let position = offset;
      while (cards.length < count) {
        const batch = await fetchGroup(mode, Math.floor(position / size) + 1);
        const part = batch.slice(position % size, position % size + count - cards.length);
        if (!part.length) throw new Error('Collection ordering page is incomplete.');
        cards.push(...part);
        position += part.length;
      }
      return cards;
    };

    const inStart = Math.min(start, stockCount);
    const inEnd = Math.min(end, stockCount);
    const outStart = Math.max(start - stockCount, 0);
    const outEnd = Math.max(end - stockCount, 0);
    const groups = await Promise.all([
      groupSlice('1', inStart, inEnd - inStart),
      groupSlice('0', outStart, outEnd - outStart)
    ]);
    const ordered = groups.flat();
    if (signal?.aborted || (livePanel && !panel.isConnected)) return;
    if (ordered.length !== end - start
        || new Set(ordered.map((card) => card.dataset.aonskProductId)).size !== ordered.length
        || groups[0].some((card) => card.dataset.aonskAvailable !== 'true')
        || groups[1].some((card) => card.dataset.aonskAvailable !== 'false')) {
      throw new Error('Collection ordering could not be verified.');
    }
    original.forEach((card, index) => {
      if (ordered[index]) card.replaceWith(ordered[index]);
      else card.remove();
    });
    ordered.slice(original.length).forEach((card) => grid.appendChild(card));
    panel.dataset.ordered = 'true';
  };

  const setupTools = () => {
    const desktop = window.matchMedia('(min-width: 750px)');
    document.querySelectorAll('[data-aonsk-mobile-tools]').forEach((details) => {
      const resize = () => {
        details.open = desktop.matches;
        details.toggleAttribute('data-desktop-ready', desktop.matches);
      };
      resize();
      desktop.addEventListener('change', resize);
    });
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#seed-finder-tools-"]');
      if (!link) return;
      const target = document.getElementById(link.hash.slice(1));
      const details = target?.closest('[data-aonsk-mobile-tools]');
      if (details) details.open = true;
    });
  };

  const start = async () => {
    setupTools();
    const root = document.querySelector('[data-stock-ordering]');
    if (!root) return;
    root.dataset.pauseInfiniteScroll = 'true';
    root.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    root.aonskOrderingController = controller;
    try {
      await window.aonskOrderCollectionPage(root, window.location.href, controller.signal);
      if (controller.signal.aborted) return;
      if (root.reload) root.reload();
      window.initLazyImages?.();
    } catch (error) {
      if (!controller.signal.aborted) console.warn('Collection stock ordering unavailable; displaying original results.', error);
    } finally {
      root.removeAttribute('aria-busy');
      if (!controller.signal.aborted) root.dataset.pauseInfiniteScroll = 'false';
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
